import notifier from '@albert-io/notifier';
import type {DraftInfo, GetState, SetState} from './types';
import {patchDraftGuess, saveDraftGuess} from '../../QuestionType.queries';
import {
  SessionEndedDuringDraftLoadError,
  getDraftGuessWithSessionRecovery,
  shouldReportDraftLoadFailure
} from '../../draftGuessSessionRecovery';
import {saveStatus} from 'client/components/QuestionTypes/QuestionType.types';
import isGuessOrDraftGuessEmpty from 'resources/extensions/isGuessOrDraftGuessEmpty';

// One draft row exists per (question, assignment), so a save must not start until the
// one before it has registered the draft's id. While one is in flight the rest collapse
// to a single trailing save — a draft only ever needs its newest content.
const inFlightByQuestionId = new Map<string, Promise<void>>();
const queuedContentByQuestionId = new Map<string, unknown>();

const actions = (set: SetState, get: GetState) => ({
  hasDraftGuess: (questionId) => {
    const draftGuess = get().draftByQuestionId[questionId]?.draft || null;
    return draftGuess !== null;
  },
  buildTimeElapsed: (questionId: string) => {
    const draftInfo = get().draftByQuestionId[questionId];
    if (!draftInfo) return 0;

    const {editStartTime, draft} = draftInfo;
    const prevTimeElapsed = draft?.getTimeElapsed() || 0;

    if (!editStartTime) return prevTimeElapsed;

    const newTimeElapsed = Date.now() - editStartTime.getTime();
    return newTimeElapsed + prevTimeElapsed;
  }, // time
  loadDraftGuess: async (assignmentId: string | null, questionId: string) => {
    if (!assignmentId) return;
    if (!questionId) return;
    let draftInfo: DraftInfo;

    try {
      const draftGuessResponse = await getDraftGuessWithSessionRecovery(questionId, assignmentId);
      draftInfo = {
        draft: draftGuessResponse.first(),
        saveStatus: saveStatus.saved,
        editStartTime: null
      };
    } catch (err) {
      if (shouldReportDraftLoadFailure(err)) {
        notifier.notify(err, {
          message: 'Draft guess failed to load',
          tags: ['draft-guess'],
          context: {questionId, assignmentId, status: (err as {status?: number})?.status}
        });
      }
      draftInfo = {
        draft: null,
        saveStatus: saveStatus.loadError,
        editStartTime: null
      };
    }

    set((state) => {
      state.draftByQuestionId[questionId] = draftInfo;
    });

    return draftInfo.draft;
  },
  saveDraftGuess: async (assignmentId: string | null, questionId: string, draftContent: any) => {
    if (!assignmentId) return;

    const runSave = async (content: any) => {
      // A failed load or a failed create hides the row the server already holds; creating
      // another would violate the one-draft-per-question constraint, so re-check first.
      const current = get().draftByQuestionId[questionId];
      const failedLoad = Boolean(
        current && !current.draft && current.saveStatus === saveStatus.loadError
      );
      const failedSave = Boolean(
        current && !current.draft && current.saveStatus === saveStatus.saveError
      );

      if (failedLoad || failedSave) {
        let recovered = null;
        try {
          const response = await getDraftGuessWithSessionRecovery(questionId, assignmentId);
          recovered = response.first() || null;
        } catch (err) {
          if (err instanceof SessionEndedDuringDraftLoadError) return;
          recovered = null;
        }

        // A failed *load* leaves the editor blank, so `content` here is that blank. Patching
        // it over the answer the server still holds would erase it (item-bank clears to `{}`,
        // ordering resets to the default order), so hold until a reload restores the answer.
        if (failedLoad && recovered && !isGuessOrDraftGuessEmpty(recovered)) {
          set((state) => {
            if (state.draftByQuestionId[questionId]) {
              state.draftByQuestionId[questionId].saveStatus = saveStatus.loadError;
            }
          });
          return;
        }

        // Adopt the recovered row (an empty one, or a create that failed after a real save)
        // so the save patches it instead of creating a duplicate, keeping the edit's timing.
        if (recovered) {
          set((state) => {
            state.draftByQuestionId[questionId] = {
              draft: recovered,
              saveStatus: saveStatus.saving,
              editStartTime: current ? current.editStartTime : null
            };
          });
        }
      }
      const currentDraft = get().draftByQuestionId[questionId]?.draft || null;

      set((state) => {
        if (!state.draftByQuestionId[questionId]) {
          state.draftByQuestionId[questionId] = {
            draft: null,
            saveStatus: saveStatus.saving,
            editStartTime: null
          };
        } else {
          state.draftByQuestionId[questionId].saveStatus = saveStatus.saving;
        }
      });

      const payload = {
        timeElapsed: get().buildTimeElapsed(questionId),
        content
      };

      try {
        let draftPromise = null;
        if (!currentDraft) {
          draftPromise = saveDraftGuess(payload, questionId, assignmentId);
        } else {
          draftPromise = patchDraftGuess(currentDraft.getId(), payload);
        }
        set({
          draftGuessPromise: draftPromise
        });
        const draftGuess = await draftPromise;
        set((state) => {
          state.draftByQuestionId[questionId] = {
            ...state.draftByQuestionId[questionId],
            draft: draftGuess,
            saveStatus: saveStatus.saved
          };
        });
        // Restart the edit timer after saving so we can track additional time spent on this question.
        get().startTimer(questionId);
      } catch (_err) {
        set((state) => {
          if (state.draftByQuestionId[questionId]) {
            state.draftByQuestionId[questionId].saveStatus = saveStatus.saveError;
          }
        });
      } finally {
        set((state) => {
          state.draftGuessPromise = null;
        });
      }
    };

    const inFlight = inFlightByQuestionId.get(questionId);
    if (inFlight) {
      queuedContentByQuestionId.set(questionId, draftContent);
      return inFlight;
    }

    const chain = (async () => {
      let content = draftContent;
      // A body-level finally (unlike a promise .finally) runs in the same microtask as the
      // final queued-content check; deferring the delete by even one microtask would let a
      // save queue behind a chain that has already exited its loop and never be sent.
      try {
        for (;;) {
          await runSave(content);
          if (!queuedContentByQuestionId.has(questionId)) break;
          content = queuedContentByQuestionId.get(questionId);
          queuedContentByQuestionId.delete(questionId);
        }
      } finally {
        inFlightByQuestionId.delete(questionId);
      }
    })();

    inFlightByQuestionId.set(questionId, chain);
    return chain;
  },
  shouldNotSaveDraft: () => true,
  startTimer: (questionId) => {
    set((state) => {
      if (!state.draftByQuestionId[questionId]) {
        state.draftByQuestionId[questionId] = {
          draft: null,
          saveStatus: saveStatus.saving,
          editStartTime: new Date()
        };
      } else {
        state.draftByQuestionId[questionId].editStartTime = new Date();
      }
    });
  }
});

export default actions;
